import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DateRange } from 'react-day-picker';

import {
  RiAddLine,
  RiDatabase2Line,
  RiPriceTag3Line,
  RiRefreshLine,
  RiSettings3Line,
} from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { DateRangePicker } from '@/design-system/date-picker';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { SearchSelect } from '@/design-system/search-select';
import { Switch } from '@/design-system/switch';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';
import { isUjpApprover, isUjpManager } from '@/config/ujp-permissions';
import { useAuth } from '@/contexts/AuthContext';
import {
  useUrlBool,
  useUrlNumber,
  useUrlParam,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { toast } from '@/design-system/toaster';
import {
  UJP_STATUSES,
  ujpAPI,
  type CreateUjpResult,
  type UjpCountKey,
  type UjpDetail,
  type UjpListParams,
  type UjpRow,
  type UjpStatus,
} from '@/services/api/ujp';

import CreateUjpModal from './components/CreateUjpModal';
import { UjpAgeBadge, UjpStatusBadge } from './components/UjpBadges';
import UjpDetailPanel from './components/UjpDetailPanel';
import { UJP_COPY } from './copy';
import { formatDate, formatRupiah, parseIsoDate, toIsoDate } from './format';

/**
 * Pengajuan UJP — the queue (DD6). Tabs Menunggu · Disetujui · Ditolak ·
 * Dibatalkan · Semua with server counts, default Menunggu; client / date /
 * search filters and paging all round-trip through the URL (`hooks/urlState`)
 * so a finance approver can paste "look at this one" as `?detail=<id>`.
 */

type TabKey = UjpCountKey;

const TABS: TabKey[] = [...UJP_STATUSES, 'ALL'];
const TAB_KEYS = TABS as readonly TabKey[];
const TAB_RESET_PARAMS = ['q'] as const;
const DEFAULT_PAGE_SIZE = 20;

const codeCls =
  'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
const metaCls = 'text-[11px] text-text-sub-600';

export interface UjpOverlayContext {
  detailId: string | null;
  closeDetail: () => void;
  openDetail: (id: string) => void;
  createOpen: boolean;
  openCreate: () => void;
  closeCreate: () => void;
  /** The UJP being edited in place (CR-3); null for a fresh create. */
  editing: UjpDetail | null;
  openEdit: (detail: UjpDetail) => void;
  onCreated: (result: CreateUjpResult) => void;
  onUpdated: () => void;
  setTab: (tab: TabKey) => void;
  refetch: () => void;
}

export interface UjpPageProps {
  /**
   * Overrides the default panel + wizard. The page keeps the URL state either
   * way, so a test can swap in a probe without re-implementing the queue.
   */
  renderOverlays?: (ctx: UjpOverlayContext) => React.ReactNode;
}

const UjpPage: React.FC<UjpPageProps> = ({ renderOverlays }) => {
  const c = UJP_COPY.page;

  const [tab, setTab] = useUrlParam<TabKey>('tab', 'SUBMITTED', {
    allowed: TAB_KEYS,
    resetPage: true,
    resetParams: TAB_RESET_PARAMS,
    history: 'push',
  });
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });
  const [clientId, setClientId] = useUrlParam<string>('clientId', '', {
    resetPage: true,
  });
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const deliveryFrom = searchParams.get('from') ?? '';
  const deliveryTo = searchParams.get('to') ?? '';
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });
  const [detailId, setDetailId] = useUrlParam<string>('detail', '', {
    history: 'push',
  });
  // 7A / 11A: "Spend Control bermasalah" is for approvers / managers only
  // (the build-time allowlists — a UI affordance; the chip is for everyone).
  const { user } = useAuth();
  const canFilterSpend =
    isUjpApprover(user?.email) || isUjpManager(user?.email);
  const [spendIssueParam, setSpendIssue] = useUrlBool(
    'spendControlIssue',
    false,
    { resetPage: true }
  );
  const spendIssue = canFilterSpend && spendIssueParam;

  const [items, setItems] = useState<UjpRow[]>([]);
  const [counts, setCounts] = useState<Record<UjpCountKey, number> | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UjpDetail | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const clients = useProvidersDropdown(true);

  const filterParams = useMemo(() => {
    const params: Omit<UjpListParams, 'page' | 'limit' | 'status'> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    const cid = Number(clientId);
    if (clientId && Number.isFinite(cid) && cid > 0) params.clientId = cid;
    if (deliveryFrom) params.deliveryFrom = deliveryFrom;
    if (deliveryTo) params.deliveryTo = deliveryTo;
    if (spendIssue) params.spendControlIssue = true;
    return params;
  }, [debouncedSearch, clientId, deliveryFrom, deliveryTo, spendIssue]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params: UjpListParams = { ...filterParams, page, limit: pageSize };
    if (tab !== 'ALL') params.status = tab as UjpStatus;
    try {
      const res = await ujpAPI.list(params);
      setItems(res.data);
      setTotal(res.pagination.total);
      setCounts(res.counts);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : c.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize, tab, c.loadFailed]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // "/" focuses the search box (same as the other list pages).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (inEditable) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const setDateRange = (range: DateRange | undefined) => {
    patchUrl((params) => {
      if (range?.from) params.set('from', toIsoDate(range.from));
      else params.delete('from');
      if (range?.to) params.set('to', toIsoDate(range.to));
      else params.delete('to');
      params.delete('page');
    });
  };

  const hasFilters = !!(
    debouncedSearch ||
    clientId ||
    deliveryFrom ||
    deliveryTo ||
    spendIssue
  );
  const clearFilters = () => {
    setSearch('');
    patchUrl((params) => {
      ['q', 'clientId', 'from', 'to', 'spendControlIssue', 'page'].forEach((k) =>
        params.delete(k)
      );
    });
  };

  const openEdit = (detail: UjpDetail) => {
    setDetailId('');
    setEditing(detail);
    setCreateOpen(true);
  };

  /**
   * Submit lands the operator on the new row (DD6): the queue goes back to
   * Menunggu, refetches, and the panel opens on `?detail=<id>` so the toast's
   * "Lihat" and the screen agree.
   */
  const handleCreated = (result: CreateUjpResult) => {
    setEditing(null);
    setTab('SUBMITTED');
    fetchList();
    setDetailId(result.id);
    toast.success(UJP_COPY.toast.submitted(result.referenceId), {
      action: {
        label: UJP_COPY.toast.submittedView,
        onClick: () => setDetailId(result.id),
      },
    });
  };

  /**
   * A requester edit / resubmit (CR-3) refetches the list and reopens the panel
   * on the same row so the change (or the resubmitted status) is visible.
   */
  const handleUpdated = () => {
    const id = editing?.id;
    setEditing(null);
    setCreateOpen(false);
    fetchList();
    if (id) setDetailId(id);
  };

  const overlayCtx: UjpOverlayContext = {
    detailId: detailId || null,
    closeDetail: () => setDetailId(''),
    openDetail: setDetailId,
    createOpen,
    openCreate: () => setCreateOpen(true),
    closeCreate: () => {
      setCreateOpen(false);
      setEditing(null);
    },
    editing,
    openEdit,
    onCreated: handleCreated,
    onUpdated: handleUpdated,
    setTab,
    refetch: fetchList,
  };

  const columns: TableDataColumn<UjpRow>[] = [
    {
      key: 'referenceId',
      title: c.columns.ref,
      render: (_v, r) => <span className={`${codeCls} text-accent`}>{r.referenceId}</span>,
    },
    {
      key: 'client',
      title: c.columns.client,
      render: (_v, r) => <span className={nameCls}>{r.client?.name ?? '—'}</span>,
    },
    {
      key: 'deliveryDate',
      title: c.columns.deliveryDate,
      render: (_v, r) => (
        <span className="text-[13px] tabular-nums text-text-sub-600 whitespace-nowrap">
          {formatDate(r.deliveryDate)}
        </span>
      ),
    },
    {
      key: 'route',
      title: c.columns.route,
      render: (_v, r) => (
        <div className="min-w-0">
          <p className={`${nameCls} truncate max-w-[280px]`}>
            {r.origin} <span className="text-text-soft-400">→</span> {r.destination}
          </p>
          <p className={`${metaCls} flex items-center gap-1.5`}>
            <span>{r.stopCount} stop</span>
            {r.isReverse && (
              <Badge status="feature" size="sm">
                {c.reverse}
              </Badge>
            )}
          </p>
        </div>
      ),
    },
    {
      key: 'driver',
      title: c.columns.driver,
      render: (_v, r) => {
        const name = r.payeeType === 'subcon' ? r.vendor?.name : r.rider?.name;
        return (
          <div>
            <p className={nameCls}>{name ?? '—'}</p>
            <p className={metaCls}>
              {r.payeeType === 'subcon' ? 'Subcon' : 'Driver'} · {r.plateNumber}
            </p>
          </div>
        );
      },
    },
    {
      key: 'total',
      title: c.columns.total,
      align: 'right',
      render: (_v, r) => (
        <div className="flex flex-col items-end gap-0.5">
          {r.masked || !r.totals ? (
            <Badge status="neutral" size="sm">
              {c.hidden}
            </Badge>
          ) : (
            <span className="font-mono text-[12px] font-semibold tabular-nums text-text-strong-950 whitespace-nowrap">
              {formatRupiah(r.totals.total)}
            </span>
          )}
          {r.revenue &&
            (r.revenue.status !== 'OK' ? (
              <Badge status="warning" size="sm">
                {c.revenueMissing}
              </Badge>
            ) : r.revenue.margin != null ? (
              <span
                className={`font-mono text-[11px] tabular-nums whitespace-nowrap ${
                  Number(r.revenue.margin) < 0
                    ? 'text-error-base'
                    : 'text-text-soft-400'
                }`}
              >
                {c.marginPrefix} {formatRupiah(r.revenue.margin)}
              </span>
            ) : null)}
        </div>
      ),
    },
    {
      key: 'ujpStatus',
      title: c.columns.status,
      render: (_v, r) => (
        <div className="flex flex-col items-start gap-1">
          <UjpStatusBadge status={r.status} />
          {/* CR-7: a biaya tambahan is waiting on finance for this trip. */}
          {(r.pendingAmendments ?? 0) > 0 && (
            <Badge
              status="away"
              size="sm"
              title={UJP_COPY.amendment.pendingChipCount(r.pendingAmendments ?? 0)}
            >
              {UJP_COPY.amendment.pendingChip}
            </Badge>
          )}
          {/* 7A: a Spend Control request (UJP or biaya tambahan) needs a look;
              FAILED already wins over UNKNOWN server-side. */}
          {r.spendControlIssue === 'FAILED' ? (
            <Badge status="error" size="sm" data-testid="ujp-row-spend-issue">
              {UJP_COPY.spend.chipFailed}
            </Badge>
          ) : r.spendControlIssue === 'UNKNOWN' ? (
            <Badge status="warning" size="sm" data-testid="ujp-row-spend-issue">
              {UJP_COPY.spend.chipUnknown}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'age',
      title: c.columns.age,
      render: (_v, r) => <UjpAgeBadge createdAt={r.createdAt} />,
    },
  ];

  const emptyState = error ? (
    <EmptyState size="sm">
      <EmptyStateTitle>{c.loadFailed}</EmptyStateTitle>
      <EmptyStateDescription>{error}</EmptyStateDescription>
      <EmptyStateActions>
        <Button tone="neutral" style="ghost" size="sm" onClick={fetchList}>
          {c.retry}
        </Button>
      </EmptyStateActions>
    </EmptyState>
  ) : hasFilters ? (
    <EmptyState size="sm">
      <EmptyStateTitle>
        {c.filteredEmpty(
          debouncedSearch ||
            clients.labelFor(clientId) ||
            (spendIssue ? UJP_COPY.spend.filter : '…')
        )}
      </EmptyStateTitle>
      <EmptyStateActions>
        <Button tone="neutral" style="ghost" size="sm" onClick={clearFilters}>
          {c.clearFilter}
        </Button>
      </EmptyStateActions>
    </EmptyState>
  ) : (
    <EmptyState size="sm">
      <EmptyStateTitle>{c.empty}</EmptyStateTitle>
      <EmptyStateDescription>{c.emptyHint}</EmptyStateDescription>
      <EmptyStateActions>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <RiAddLine />
          {c.create}
        </Button>
      </EmptyStateActions>
    </EmptyState>
  );

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">{c.eyebrow}</p>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-text-strong-950">
            {c.title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-sub-600">
            {c.subtitle}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchList}>
            <RiRefreshLine />
            {c.refresh}
          </Button>
          <Button tone="neutral" style="stroke" asChild>
            <Link to="/ujp/config">
              <RiSettings3Line />
              {c.config}
            </Link>
          </Button>
          <Button tone="neutral" style="stroke" asChild>
            <Link to="/ujp/tariffs">
              <RiPriceTag3Line />
              {c.tariffs}
            </Link>
          </Button>
          <Button tone="neutral" style="stroke" asChild>
            <Link to="/ujp/masters">
              <RiDatabase2Line />
              {c.masters}
            </Link>
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <RiAddLine />
            {c.create}
          </Button>
        </div>
      </div>

      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList variant="line" className="h-auto min-h-10 w-auto flex-wrap border-0">
              {TABS.map((t) => (
                <TabsTrigger key={t} value={t}>
                  {UJP_COPY.tabs[t]}
                  {counts && counts[t] != null && <TabsCount>{counts[t]}</TabsCount>}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 flex-wrap items-center justify-end gap-2 lg:py-2">
            <SearchField
              ref={searchInputRef}
              value={search}
              onChange={setSearch}
              placeholder={c.searchPlaceholder}
              size="sm"
              className="min-w-[220px] max-w-xs flex-1"
            />
            <div className="w-56">
              <SearchSelect
                options={clients.options.map((p) => ({ value: String(p.id), label: p.name }))}
                value={clientId || undefined}
                selectedLabelFallback={clientId ? clients.labelFor(clientId) : undefined}
                onChange={(v) => setClientId(v ? String(v) : '')}
                placeholder={c.clientFilter}
                emptyMessage={clients.error ?? 'Client tidak ditemukan'}
                loading={clients.loading}
                hasMore={clients.hasMore}
                disableLocalFilter
                onSearchChange={clients.setSearch}
                onLoadMore={clients.loadMore}
              />
            </div>
            <DateRangePicker
              size="sm"
              placeholder="Tgl kirim"
              value={
                deliveryFrom || deliveryTo
                  ? { from: parseIsoDate(deliveryFrom), to: parseIsoDate(deliveryTo) }
                  : undefined
              }
              onValueChange={setDateRange}
            />
            {canFilterSpend && (
              <label className="flex cursor-pointer items-center gap-2 text-[12px] text-text-sub-600">
                <Switch
                  aria-label={UJP_COPY.spend.filter}
                  checked={spendIssueParam}
                  onCheckedChange={setSpendIssue}
                  data-testid="ujp-filter-spend-issue"
                />
                {UJP_COPY.spend.filter}
              </label>
            )}
          </div>
        </div>

        <div className="p-4">
          <TableData<UjpRow>
            data={error ? [] : items}
            columns={columns}
            loading={loading}
            size="sm"
            emptyState={emptyState}
            onRowClick={(r) => setDetailId(r.id)}
          />
          {!error && total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      </Card>

      {renderOverlays ? (
        renderOverlays(overlayCtx)
      ) : (
        <>
          <UjpDetailPanel
            ujpId={detailId || null}
            onClose={() => setDetailId('')}
            onChanged={fetchList}
            onEdit={openEdit}
          />
          <CreateUjpModal
            open={createOpen}
            onClose={() => {
              setCreateOpen(false);
              setEditing(null);
            }}
            editing={editing}
            onCreated={handleCreated}
            onUpdated={handleUpdated}
          />
        </>
      )}
    </div>
  );
};

export default UjpPage;
